import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell, Button, Icon, SelectMenu } from '../../ui';
import { useCategorias } from '../../servicos/categorias/useCategorias';
import { useContas } from '../../servicos/contas/useContas';
import { useCartaoCreditos } from '../../servicos/cartaoCredito/useCartaoCredito';
import { useImportacoes } from '../../servicos/importacoes/useImportacoes';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import useAvisos from '../../hooks/useAviso';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { valorParaTexto } from '../../utils/Utils';
import { deISO } from '../movimentacoes/transacaoForm';
import {
  ImportacaoAnalisada,
  LinhaRevisao,
  montarTransacoesDaImportacao,
  prepararLinhas,
  totalSelecionado
} from './logica';
import styles from './Importar.module.css';

const API_TRANSACOES = process.env.REACT_APP_API_URL + '/transacoes';

type Etapa = 'upload' | 'revisao' | 'concluido';

const diaMes = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const periodoLegivel = (inicio?: string | null, fim?: string | null) => {
  if (!inicio || !fim) return null;
  const f = (iso: string) =>
    deISO(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  return `${f(inicio)} a ${f(fim)}`;
};

/**
 * Importação de PDF (fatura de cartão ou extrato de conta) lida por IA.
 * Fluxo em 3 etapas: enviar → revisar linha a linha → confirmar.
 * Nada entra no app sem passar pela revisão humana.
 */
export default function Importar() {
  const navigate = useNavigate();
  const { categorias } = useCategorias();
  const { contas } = useContas();
  const { cartoes } = useCartaoCreditos();
  const { analisando, analisarPdf } = useImportacoes();
  const { setAtualizar } = useAtualizacaoContexto();
  const { adicionarAviso } = useAvisos();

  const [etapa, setEtapa] = useState<Etapa>('upload');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [cienteIa, setCienteIa] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [erroUpload, setErroUpload] = useState('');

  const [analise, setAnalise] = useState<ImportacaoAnalisada | null>(null);
  const [linhas, setLinhas] = useState<LinhaRevisao[]>([]);
  const [destino, setDestino] = useState('');
  const [tentouImportar, setTentouImportar] = useState(false);
  const [progresso, setProgresso] = useState<{ atual: number; total: number } | null>(null);
  const [resultado, setResultado] = useState({ ok: 0, falhas: 0 });

  const inputRef = useRef<HTMLInputElement>(null);

  const aoEscolherArquivo = (novo: File | null) => {
    setErroUpload('');
    if (!novo) return;
    if (!novo.name.toLowerCase().endsWith('.pdf')) {
      setErroUpload('Envie um arquivo PDF (fatura de cartão ou extrato de conta).');
      return;
    }
    if (novo.size > 4 * 1024 * 1024) {
      setErroUpload('O PDF pode ter no máximo 4 MB. Exporte apenas o período que deseja importar.');
      return;
    }
    setArquivo(novo);
  };

  const analisar = async () => {
    if (!arquivo || !cienteIa || analisando) return;
    setErroUpload('');

    const { analise: nova, erro } = await analisarPdf(arquivo);
    if (!nova) {
      setErroUpload(erro ?? 'Não consegui analisar o documento.');
      return;
    }

    // Transações já existentes no período do documento, pra pré-marcar duplicatas.
    let existentes: Transacao[] = [];
    if (nova.periodoInicio && nova.periodoFim) {
      try {
        const token = localStorage.getItem('token');
        const resposta = await axios.get<Transacao[]>(API_TRANSACOES, {
          headers: { Authorization: `Bearer ${token}` },
          params: { dataInicial: deISO(nova.periodoInicio), dataFinal: deISO(nova.periodoFim) }
        });
        existentes = resposta.data;
      } catch {
        // Sem a lista, só perdemos a pré-marcação de duplicatas — a revisão continua.
      }
    }

    setAnalise(nova);
    setLinhas(prepararLinhas(nova, existentes));
    const opcoes = nova.tipoDocumento === 'fatura_cartao' ? cartoes : contas;
    setDestino(opcoes.length === 1 ? opcoes[0].id?.toString() ?? '' : '');
    setTentouImportar(false);
    setEtapa('revisao');
  };

  const alternarLinha = (chave: string) =>
    setLinhas((atuais) =>
      atuais.map((linha) =>
        linha.chave === chave && !linha.travada
          ? { ...linha, selecionada: !linha.selecionada }
          : linha
      )
    );

  const trocarCategoria = (chave: string, categoriaId: string) =>
    setLinhas((atuais) =>
      atuais.map((linha) => (linha.chave === chave ? { ...linha, categoriaId } : linha))
    );

  const { quantidade, total } = useMemo(() => totalSelecionado(linhas), [linhas]);

  const importar = async () => {
    if (!analise || progresso) return;
    setTentouImportar(true);
    if (!destino || quantidade === 0) return;

    const payloads = montarTransacoesDaImportacao(linhas, analise.tipoDocumento, Number(destino));
    const token = localStorage.getItem('token');
    let ok = 0;
    let falhas = 0;

    for (let i = 0; i < payloads.length; i++) {
      setProgresso({ atual: i + 1, total: payloads.length });
      try {
        await axios.post(API_TRANSACOES, payloads[i], {
          headers: { Authorization: `Bearer ${token}` }
        });
        ok++;
      } catch {
        falhas++;
      }
    }

    setProgresso(null);
    setResultado({ ok, falhas });
    setAtualizar((atual) => !atual);
    if (falhas === 0) {
      adicionarAviso(`${ok} lançamento(s) importados.`, 'sucesso');
    } else {
      adicionarAviso(`${ok} lançamento(s) importados; ${falhas} não puderam ser criados. Verifique as movimentações.`);
    }
    setEtapa('concluido');
  };

  const recomecar = () => {
    setEtapa('upload');
    setArquivo(null);
    setCienteIa(false);
    setAnalise(null);
    setLinhas([]);
    setDestino('');
    setErroUpload('');
  };

  const ehFatura = analise?.tipoDocumento === 'fatura_cartao';
  const opcoesDestino = (ehFatura ? cartoes : contas).map((item) => ({
    id: item.id?.toString() ?? '',
    rotulo: ehFatura ? (item as { nome: string }).nome : (item as { descricao: string }).descricao,
    icone: ehFatura ? 'credit_card' : 'account_balance'
  }));

  const checksum = analise?.checksumConfere;

  return (
    <AppShell
      titulo="Importar documento"
      subtitulo="Traga a fatura do cartão ou o extrato da conta em PDF: a IA lê, você revisa e confirma."
    >
      {etapa === 'upload' && (
        <div className={styles.envelope}>
          <div
            className={`${styles.zona} ${arrastando ? styles.zonaAtiva : ''}`}
            onDragOver={(evento) => {
              evento.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(evento) => {
              evento.preventDefault();
              setArrastando(false);
              aoEscolherArquivo(evento.dataTransfer.files?.[0] ?? null);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              className={styles.inputEscondido}
              onChange={(evento) => aoEscolherArquivo(evento.target.files?.[0] ?? null)}
            />
            <div className={styles.zonaIcone}>
              <Icon nome={arquivo ? 'task' : 'upload_file'} tamanho={40} />
            </div>
            {arquivo ? (
              <>
                <span className={styles.zonaTitulo}>{arquivo.name}</span>
                <span className={styles.zonaDica}>
                  {(arquivo.size / 1024).toFixed(0)} KB · clique para trocar o arquivo
                </span>
              </>
            ) : (
              <>
                <span className={styles.zonaTitulo}>Arraste o PDF aqui ou clique para escolher</span>
                <span className={styles.zonaDica}>
                  Fatura de cartão ou extrato de conta, de qualquer banco, até 4 MB
                </span>
              </>
            )}
          </div>

          <label className={styles.consentimento}>
            <input
              type="checkbox"
              checked={cienteIa}
              onChange={(evento) => setCienteIa(evento.target.checked)}
            />
            <span>
              Estou ciente de que o PDF é processado por inteligência artificial (Anthropic) apenas
              para extrair os lançamentos e é <strong>descartado em seguida</strong>: o documento não
              fica salvo em lugar nenhum.
            </span>
          </label>

          {erroUpload && (
            <div className={styles.erro}>
              <Icon nome="error" tamanho={18} />
              {erroUpload}
            </div>
          )}

          {analisando ? (
            <div className={styles.lendo}>
              <span className={styles.lendoGiro}>
                <Icon nome="progress_activity" tamanho={22} />
              </span>
              Analisando o documento (leva menos de um minuto)…
            </div>
          ) : (
            <Button
              variante="primaria"
              icone="auto_awesome"
              disabled={!arquivo || !cienteIa}
              onClick={analisar}
            >
              Analisar documento
            </Button>
          )}
        </div>
      )}

      {etapa === 'revisao' && analise && (
        <div className={styles.revisao}>
          <div className={styles.cabecalho}>
            <div className={styles.cabecalhoInfo}>
              <span className={styles.docIcone}>
                <Icon nome={ehFatura ? 'credit_card' : 'account_balance'} tamanho={22} />
              </span>
              <div>
                <span className={styles.docTitulo}>
                  {ehFatura ? 'Fatura de cartão' : 'Extrato de conta'}
                  {analise.banco ? ` · ${analise.banco}` : ''}
                </span>
                {periodoLegivel(analise.periodoInicio, analise.periodoFim) && (
                  <span className={styles.docPeriodo}>
                    {periodoLegivel(analise.periodoInicio, analise.periodoFim)}
                  </span>
                )}
              </div>
            </div>
            {checksum === true && (
              <span className={`${styles.selo} ${styles.seloOk}`}>
                <Icon nome="verified" tamanho={16} />
                Total confere
                {analise.totalDeclarado ? `: ${valorParaTexto(analise.totalDeclarado, true)}` : ''}
              </span>
            )}
            {checksum === false && (
              <span className={`${styles.selo} ${styles.seloAlerta}`}>
                <Icon nome="warning" tamanho={16} />
                A soma não confere com o documento
              </span>
            )}
            {(checksum === null || checksum === undefined) && (
              <span className={`${styles.selo} ${styles.seloNeutro}`}>
                <Icon nome="info" tamanho={16} />
                Sem total declarado para conferência
              </span>
            )}
          </div>

          {analise.avisos.length > 0 && (
            <div className={styles.avisos}>
              {analise.avisos.map((aviso) => (
                <div key={aviso} className={styles.aviso}>
                  <Icon nome="warning" tamanho={16} />
                  {aviso}
                </div>
              ))}
            </div>
          )}

          <div className={styles.destino}>
            <SelectMenu
              label={ehFatura ? 'Lançar no cartão' : 'Lançar na conta'}
              valor={destino}
              onChange={setDestino}
              opcoes={opcoesDestino}
              placeholder={ehFatura ? 'Selecione o cartão da fatura' : 'Selecione a conta do extrato'}
              erro={tentouImportar && !destino ? 'Escolha o destino dos lançamentos.' : undefined}
            />
          </div>

          <div className={styles.lista}>
            {linhas.map((linha) => {
              const { lancamento } = linha;
              const inativa = !linha.selecionada || linha.travada;
              return (
                <div key={linha.chave} className={`${styles.linha} ${inativa ? styles.linhaInativa : ''}`}>
                  <button
                    type="button"
                    className={`${styles.caixa} ${linha.selecionada ? styles.caixaMarcada : ''}`}
                    onClick={() => alternarLinha(linha.chave)}
                    disabled={linha.travada}
                    aria-label={linha.selecionada ? 'Não importar este lançamento' : 'Importar este lançamento'}
                  >
                    {linha.selecionada && <Icon nome="check" tamanho={14} />}
                  </button>
                  <span className={styles.linhaData}>{diaMes(lancamento.data)}</span>
                  <div className={styles.linhaCentro}>
                    <span className={styles.linhaDescricao}>{lancamento.descricao}</span>
                    <span className={styles.linhaEtiquetas}>
                      {lancamento.parcela && <em className={styles.etiqueta}>parcela {lancamento.parcela}</em>}
                      {linha.duplicata && (
                        <em className={`${styles.etiqueta} ${styles.etiquetaDuplicata}`}>possível duplicata</em>
                      )}
                      {lancamento.ehPagamentoDeFatura && (
                        <em className={`${styles.etiqueta} ${styles.etiquetaFatura}`}>
                          {linha.travada ? 'quitação da fatura anterior, não será importada' : 'pagamento de fatura'}
                        </em>
                      )}
                    </span>
                  </div>
                  {!linha.travada && !lancamento.ehPagamentoDeFatura && (
                    <div className={styles.linhaCategoria}>
                      <SelectMenu
                        valor={linha.categoriaId}
                        onChange={(id) => trocarCategoria(linha.chave, id)}
                        opcoes={categorias
                          .filter((cat) => cat.tipo === lancamento.tipo)
                          .map((cat) => ({
                            id: cat.id?.toString() ?? '',
                            rotulo: cat.nome,
                            icone: cat.icone || 'category',
                            cor: cat.corIdentificacao
                          }))}
                        placeholder="Categoria"
                        busca
                      />
                    </div>
                  )}
                  <span
                    className={`${styles.linhaValor} ${lancamento.tipo === 'R' ? styles.valorReceita : ''}`}
                  >
                    {lancamento.tipo === 'R' ? '+' : '−'}
                    {valorParaTexto(lancamento.valor, true)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.rodape}>
            <Button variante="fantasma" icone="arrow_back" onClick={recomecar}>
              Voltar
            </Button>
            <div className={styles.rodapeResumo}>
              <span>{quantidade} selecionado(s)</span>
              <strong className={total >= 0 ? styles.valorReceita : undefined}>
                {valorParaTexto(total, true)}
              </strong>
            </div>
            <Button
              variante="primaria"
              icone="download_done"
              carregando={!!progresso}
              disabled={quantidade === 0}
              onClick={importar}
            >
              {progresso ? `Importando ${progresso.atual}/${progresso.total}…` : `Importar ${quantidade} lançamento(s)`}
            </Button>
          </div>
        </div>
      )}

      {etapa === 'concluido' && (
        <div className={styles.concluido}>
          <span className={styles.concluidoIcone}>
            <Icon nome="check_circle" tamanho={56} />
          </span>
          <h2 className={styles.concluidoTitulo}>
            {resultado.ok} lançamento(s) importados
            {resultado.falhas > 0 ? ` · ${resultado.falhas} falharam` : ''}
          </h2>
          <p className={styles.concluidoTexto}>
            Importação concluída. Os números do aplicativo já refletem os novos lançamentos.
          </p>
          <div className={styles.concluidoAcoes}>
            <Button variante="contorno" icone="upload_file" onClick={recomecar}>
              Importar outro PDF
            </Button>
            <Button variante="primaria" icone="currency_exchange" onClick={() => navigate('/movimentacoes')}>
              Ver movimentações
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
